<template>
  <div class="col-12 nopads">
    <div class="d-flex flex-row w-100">
      <div
        class="order-text ps-2 border-top"
        :style="{width: '100%', minWidth: textColWidthPercentage + '%', marginTop: '-2px', marginBottom: '-2px', borderTop: '1px #BCBCBC solid', borderBottom: '1px #BCBCBC solid'}"
      >
        <div class="row col-12 !nopads">
          <div class="col-9 !nopads single-line">
            <font-awesome-icon
              icon="caret-right"
              class="pointer ms-1"
              style="outline: none; width: 2.5em; height: 1.1em"
              :class="{ 'fa-rotate-90': showInfo }"
              @click.stop="toggleExtraInfo"
            />
            <span
              class="ms-2"
              :class="{'disabled-contract' : disableContractSelection}"
            >
              {{ $t('contracts.duty_officer_schedule') }}
            </span>
          </div>
        </div>
      </div>
      <div style="min-width: 980px; margin-top: -.1em" class="border-top" />
    </div>
    <div v-show="showInfo">
      <div>
        <!-- One row per user (grouped), showing all of that user's schedules as bars -->
        <div
          v-for="(group, groupIndex) in groupedSchedules"
          :key="'user-' + group.duty_officer.id"
          class="d-flex flex-row w-100"
        >
          <!-- Text part -->
          <div
            class="row order-text sub-item pointer"
            :style="{
              width: '100%',
              minWidth: textColWidthPercentage + '%',
              color: '#636364',
              borderTop: '1px #BCBCBC solid',
              borderBottom: (groupIndex === groupedSchedules.length - 1 && draftRows.length === 0) ? '1px #BCBCBC solid' : 'none'
            }"
            @click.stop="selectUser(group)"
          >
            <div class="row ps-3">
              <div
                class="col-12"
                style="padding-left: 2em;"
              >
                {{ group.duty_officer.first_name }} {{ group.duty_officer.last_name }}
                <span class="text-muted">({{ group.schedules.length }})</span>
              </div>
            </div>
          </div>

          <!-- Time line: all of this user's schedules as bars on one track -->
          <div
            :style="{
              display: 'inline-block',
              borderBottom: (groupIndex === groupedSchedules.length - 1 && draftRows.length === 0) ? '1px #BCBCBC solid' : 'none'
            }"
          >
            <user-duty-officer-timeline
              :schedules="group.schedules"
              :time-col-width="weekColWidth"
              :calendar-start-date="stableCalendarStartDate"
              :visible-weeks-limit="visibleWeeksLimit"
              :calendar-start-week="calendarStartWeek"
              @dates-changed="updateScheduleDates"
            />
          </div>
        </div>

        <!-- Draft rows (new, unsaved): click week cells to pick a range, then confirm -->
        <div
          v-for="draft in draftRows"
          :key="'new-' + draft.tempId"
          class="d-flex flex-row w-100"
        >
          <!-- Text part -->
          <div
            class="row order-text sub-item"
            :style="{width: '100%', minWidth: textColWidthPercentage + '%', color: '#636364', borderTop: '1px #BCBCBC solid' }"
          >
            <div class="row ps-3">
              <div
                class="col-9"
                style="padding-left: 2em;"
              >
                <div class="d-flex flex-column">
                  <span>{{ draft.duty_officer.first_name }} {{ draft.duty_officer.last_name }}</span>
                  <span class="text-danger mt-2">{{ $t('calendar.select_dates_hint') }}</span>
                </div>
              </div>
              <div class="col-3 text-end pe-2">
                <i
                  class="fa fa-trash pointer mt-1"
                  data-bs-toggle="tooltip"
                  :title="$t('common.remove')"
                  @click.stop="removeNewRow(draft.tempId)"
                />
              </div>
            </div>
          </div>

          <!-- Time line: click week cells to pick a range (first click = start, second = end) -->
          <div style="display: inline-block;">
            <div style="display: flex; position: relative;">
              <div
                v-for="(week, index) in weeks"
                :key="index"
                class="week-item week-selectable pointer"
                :class="{ 'week-selected': isWeekSelected(draft, index) }"
                :style="{ width: weekColWidth + 'px' }"
                @click.stop="selectWeek(draft, index)"
              >
                -
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- User selector + add button -->
      <div class="d-flex flex-row align-items-start mt-3 mb-2 ps-5">
        <div style="min-width: 220px; max-width: 320px;" class="ms-2">
          <multiselect
            v-model="selectedUser"
            :placeholder="$t('trip_list.select_user')"
            :deselect-label="$t('common.remove_selection')"
            :select-label="$t('common.select')"
            :close-on-select="true"
            :multiple="false"
            label="name"
            :custom-label="userName"
            track-by="id"
            :searchable="true"
            :internal-search="true"
            :selected-label="$t('common.selected')"
            :options="users"
          />
        </div>
        <button
          class="btn btn-sm btn-success ms-2"
          :disabled="!selectedUser"
          @click.stop="appendDutyOfficerSchedules"
        >
          <i
            class="fa fa-plus"
            style="font-size: .8em; padding: 0; line-height: 1em;"
          />
        </button>
      </div>
    </div>

    <!-- Confirm new schedule modal -->
    <base-modal
      v-if="confirmingSchedule"
      :title="$t('common.confirm')"
      :hide-footer="true"
      size="sm"
      :allow-overflow="true"
      @close="cancelConfirm"
    >
      <div class="col-12">
        <span class="item-detail-title">{{ $t('common.start_date') }}</span>
        <date-time-picker
          v-model:date="confirmStartDate"
          format="dd.MM.yyyy HH:mm"
          :enable-time-picker="true"
        />
        <span class="item-detail-title mt-2">{{ $t('common.end_date') }}</span>
        <date-time-picker
          v-model:date="confirmEndDate"
          format="dd.MM.yyyy HH:mm"
          :enable-time-picker="true"
          :min-date="confirmStartDate"
        />
        <div class="col-12 text-end mt-3 mb-2">
          <button
            class="btn btn-sm btn-secondary form-button"
            @click.stop="cancelConfirm"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-sm btn-success form-button"
            :disabled="!confirmStartDate || !confirmEndDate || saving"
            @click.stop="confirmCreateSchedule"
          >
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </base-modal>
  </div>
</template>


<script>

import {timeUtils} from "@/components/mixins/TimeUtils";
import {restApi} from "@/components/mixins/RestApiMixin";
import UserDutyOfficerTimeline from "@/components/contractcalendar/UserDutyOfficerTimeline.vue";
import {orderMixin} from "@/components/mixins/OrderMixin";
import {workAssignmentHelper} from "@/components/mixins/WorkAssignmentMixin";
import {inject} from "vue";
import BaseModal from "@/components/BaseModal.vue";
import DateTimePicker from "@/components/common/DateTimePicker";

export default {
    name: 'DutyOfficerScheduleCalendarItem',
    components: {UserDutyOfficerTimeline, BaseModal, DateTimePicker},
    mixins: [timeUtils, restApi, orderMixin, workAssignmentHelper],
    props: {
        schedules: {
            type: Array,
            default() {
                return []
            }
        },
        textColWidthPercentage: {
            type: Number,
            default: 40
        },
        timeColWidth: {
            type: Number,
            default: 100  // px
        },
        calendarStartDate: {
            type: Date,
            default: null
        },
        visibleWeeksLimit: {
            type: Number,
            default: 20
        },
        calendarStartWeek: {
            type: Number,
            default: 10
        },
        assignmentFilter: {
            type: Array,
            default: null
        },
        autoExpand: {
            type: Boolean,
            default: false
        },
        disableContractSelection: {
            type: Boolean,
            default: false
        },
        contractId: {
            type: Number,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            start: null,
            showInfo: false,
            weeks: [],
            dutyOfficerSchedules: [],
            users: [],
            selectedUser: null,
            newRowSeq: 0,
            confirmingSchedule: null,
            confirmStartDate: null,
            confirmEndDate: null,
            saving: false,
            stableCalendarStartDate: null,
        }
    },
    computed: {
        weekColWidth() {
            return Math.round(this.timeColWidth / 7) * 7
        },
        // Saved schedules grouped by user -> one calendar row per user
        groupedSchedules() {
            const groups = new Map()
            for (const schedule of this.dutyOfficerSchedules) {
                if (schedule.id == null || !schedule.duty_officer) {
                    continue
                }
                const userId = schedule.duty_officer.id
                if (!groups.has(userId)) {
                    groups.set(userId, { duty_officer: schedule.duty_officer, schedules: [] })
                }
                groups.get(userId).schedules.push(schedule)
            }
            return Array.from(groups.values())
        },
        // Unsaved draft rows (one per newly added user, week-picker in progress)
        draftRows() {
            return this.dutyOfficerSchedules.filter(s => s.id == null)
        },
    },
    watch: {
        calendarStartWeek: function () {
            this.initTable()
        },
        calendarStartDate: function (newVal) {
            this.updateStableCalendarStartDate(newVal)
        },
        schedules: function (newSchedules) {
            this.mergeSavedSchedules(newSchedules || [], null)
        },
    },
    mounted: function () {
        this.initTable();
        this.updateStableCalendarStartDate(this.calendarStartDate)
        this.dutyOfficerSchedules = [...this.schedules]
        this.fetchUsers()
    },
    methods: {

        toggleExtraInfo() {
            this.showInfo = !this.showInfo;
        },

        // Clicking a user's row/timeline loads that user + their schedules into the editor form below
        selectUser(group) {
            this.$emit('selectDutyOfficerUser', {
                user: group.duty_officer,
                schedules: group.schedules,
            })
        },

        // Drag/resize on a timeline bar updates that schedule's dates via the edit endpoint
        updateScheduleDates(scheduleId, startDate, endDate) {
            const params = {
                id: scheduleId,
                start_date: this.getDateIsoString(startDate),
                end_date: this.getDateIsoString(endDate),
            }
            this.restUpdate(this.dutyOfficerScheduleEditUrl, params, () => {
                this.$emit('updateDutyOfficerSchedule')
                this.showAlert(this.$t('common.alert_update_success'), 'success')
            }, () => {
                this.showAlert(this.$t('common.alert_update_failed'))
            })
        },

        initTable() {
            this.weeks = []
            this.start = this.calendarStartWeek;
            let startWeek = this.start
            for (let i = 0; i < this.visibleWeeksLimit; i++) {
                this.weeks.push(this.$t('calendar.wk_prefix') + (startWeek + i))
            }
        },
        // Refresh the stable Date only when the underlying time value actually changes,
        // so an ancestor re-render (which passes a fresh new Date) doesn't churn child timelines.
        updateStableCalendarStartDate(value) {
            const ms = value ? value.getTime() : null
            const currentMs = this.stableCalendarStartDate ? this.stableCalendarStartDate.getTime() : null
            if (ms !== currentMs) {
                this.stableCalendarStartDate = ms != null ? new Date(ms) : null
            }
        },
        appendDutyOfficerSchedules() {
            if (!this.selectedUser) {
                return
            }
            this.dutyOfficerSchedules.push({
                id: null,
                tempId: --this.newRowSeq,
                duty_officer: {
                    id: this.selectedUser.id,
                    first_name: this.selectedUser.first_name,
                    last_name: this.selectedUser.last_name,
                },
                start_date: null,
                end_date: null,
                selStartIndex: null,
                selEndIndex: null,
            })
            this.selectedUser = null
        },
        removeNewRow(tempId) {
            this.dutyOfficerSchedules = this.dutyOfficerSchedules.filter(
                s => s.id != null || s.tempId !== tempId
            )
        },
        // Snap a date back to Monday 00:00:00 of its week
        snapToWeekStart(date) {
            const d = new Date(date)
            const day = d.getDay() // 0 = Sunday, 1 = Monday, ...
            const diff = (day === 0 ? -6 : 1 - day) // move back to Monday
            d.setDate(d.getDate() + diff)
            d.setHours(0, 0, 0, 0)
            return d
        },
        // Snap a date forward to Sunday 23:59:59 of its week
        snapToWeekEnd(date) {
            const d = new Date(date)
            const day = d.getDay()
            const diff = (day === 0 ? 0 : 7 - day) // move forward to Sunday
            d.setDate(d.getDate() + diff)
            d.setHours(23, 59, 59, 0)
            return d
        },
        // A date landing inside the week at the given cell index
        dateForWeekIndex(index) {
            const d = new Date(this.calendarStartDate)
            d.setDate(d.getDate() + index * 7)
            return d
        },
        // Click a week cell: first click anchors the start, the next click sets the end
        // and immediately opens the confirmation modal. Clicking the lone anchor clears it.
        selectWeek(row, index) {
            if (row.selStartIndex === null) {
                // Anchor the start on the first click
                row.selStartIndex = index
                row.selEndIndex = index
            } else if (row.selStartIndex === index && row.selEndIndex === index) {
                // Clicking the lone anchor again clears it
                this.clearWeekSelection(row)
            } else {
                // Second click sets the end -> open the confirmation modal automatically
                row.selEndIndex = index
                this.openConfirmModal(row)
            }
        },
        // Clear the current week selection so the user can start over
        clearWeekSelection(row) {
            row.selStartIndex = null
            row.selEndIndex = null
        },
        isWeekSelected(row, index) {
            if (row.selStartIndex === null) {
                return false
            }
            if (row.selEndIndex === null) {
                return index === row.selStartIndex
            }
            const lo = Math.min(row.selStartIndex, row.selEndIndex)
            const hi = Math.max(row.selStartIndex, row.selEndIndex)
            return index >= lo && index <= hi
        },
        openConfirmModal(row) {
            if (!row || !row.duty_officer || row.selStartIndex === null) {
                return
            }
            const lo = Math.min(row.selStartIndex, row.selEndIndex ?? row.selStartIndex)
            const hi = Math.max(row.selStartIndex, row.selEndIndex ?? row.selStartIndex)
            const weekStart = this.snapToWeekStart(this.dateForWeekIndex(lo))
            const weekEnd = this.snapToWeekEnd(this.dateForWeekIndex(hi))
            // Open the confirmation modal with editable dates
            this.confirmStartDate = weekStart
            this.confirmEndDate = weekEnd
            this.confirmingSchedule = row
        },
        cancelConfirm() {
            // Clear the draft row's week selection so re-selecting starts fresh
            // (otherwise the next click would be treated as the "end" and reopen the modal).
            if (this.confirmingSchedule && this.confirmingSchedule.id == null) {
                this.clearWeekSelection(this.confirmingSchedule)
            }
            this.confirmingSchedule = null
            this.confirmStartDate = null
            this.confirmEndDate = null
        },
        confirmCreateSchedule() {
            const row = this.confirmingSchedule
            if (!row || !row.duty_officer || !this.confirmStartDate || !this.confirmEndDate) {
                return
            }
            const startIso = this.getDateIsoString(this.confirmStartDate)
            const endIso = this.getDateIsoString(this.confirmEndDate)
            const params = {
                schedules: {
                    user: { id: row.duty_officer.id },
                    dates: [{
                        start_date: startIso,
                        end_date: endIso,
                    }],
                },
            }
            const tempId = row.tempId
            // Local saving flag so the rows block is NOT unmounted/blanked while the request runs
            this.saving = true
            this.restUpdate(this.dutyOfficerScheduleUrl + `?contract=${this.contractId}`, params, () => {
                this.saving = false
                // The create endpoint returns only "OK" (no record), so drop the draft and
                // refetch to pull in the newly created schedule with its real id.
                this.removeNewRow(tempId)
                this.cancelConfirm()
                this.$emit('updateDutyOfficerSchedule')
                this.showAlert(this.$t('common.alert_update_success'), 'success')
            }, () => {
                this.saving = false
                this.showAlert(this.$t('common.alert_update_failed'))
            })
        },
        // Merge the server's fresh saved-schedule list into local state without a full rebuild.
        // Existing saved row objects are reused (same identity) so their timelines don't recreate;
        // only new/changed rows are added or patched. Drafts other than `savedTempId` are kept.
        mergeSavedSchedules(data, savedTempId) {
            const serverList = Array.isArray(data) ? data : []
            // Keep remaining drafts, dropping the one we just saved
            const drafts = this.dutyOfficerSchedules.filter(
                s => s.id == null && s.tempId !== savedTempId
            )
            const existingById = new Map(
                this.dutyOfficerSchedules.filter(s => s.id != null).map(s => [s.id, s])
            )
            const mergedSaved = serverList.map(serverRow => {
                const existing = existingById.get(serverRow.id)
                if (existing) {
                    // Patch fields in place on the existing object so Vue reuses the same row node
                    existing.start_date = serverRow.start_date
                    existing.end_date = serverRow.end_date
                    existing.duty_officer = serverRow.duty_officer
                    return existing
                }
                return serverRow
            })
            this.dutyOfficerSchedules = [...mergedSaved, ...drafts]
        },
        userName({first_name, last_name}) {
            return `${first_name} ${last_name}`
        },
        fetchUsers() {
            this.restFetch(this.userUrl, response => {
                this.users = []
                const users = response.data
                users.forEach(user => {
                    this.users.push({
                        id: user.id,
                        first_name: user.first_name,
                        last_name: user.last_name,
                    })
                })
            })
        },
    }
}
</script>
<style scoped>
.calendar-item-container {
    user-select: none;
}

.expanded {
    background: #FFFFFF !important;
}

.contract-header-text, .week-item, .order-text {
    display: inline-block;
    background: #ffffff;
    border-top: 1px #BCBCBC solid;
    border-right: 1px #BCBCBC solid;
    margin: 0;
    padding: .1em;
    font-size: .9em;
    user-select: none;
    min-height: 2em;
}

.contract-header-text {
    font-weight: bold;
    background: #f6f5f1;
}

.order-text {
    color: #404041;
    padding-left: 2.2em;
}

.contract-header-text:hover {
    color: #007BFF;
    font-weight: bold;
    cursor: pointer;
}

.disabled-contract {
    color: #404041 !important;
    cursor: default;
}

.week-item {
    color: #FFFFFF;
    user-select: none;
    padding: .1em;
}

.week-selectable:hover {
    background: #e8eef5;
}

.week-selected {
    background: #052f5b;
    border-radius: 0;
}

.week-selected:hover {
    background: #052f5b;
}

.confirm-check {
    font-size: 1em;
}

.dropdown-adder {
    padding-top: 0;
    padding-bottom: 0;
    max-height: 1.4em;
}

.single-line {
    display: block;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pointer {
    cursor: pointer;
}

.pointer:hover {
    background: #f0f0f0;
}
</style>
